import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FilePen, ShieldCheck, Clock, FileText, TrendingUp, ArrowRight, Trash2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIGN_TYPE_COLORS = {
  PAdES: "bg-blue-50 text-blue-700 border-blue-200",
  CAdES: "bg-purple-50 text-purple-700 border-purple-200",
  XAdES: "bg-orange-50 text-orange-700 border-orange-200",
};

const STATUS_COLORS = {
  assinado: "badge-signed",
  pendente: "badge-pending",
  verificado: "badge-verified",
};

const STATUS_LABELS = {
  assinado: "Assinado",
  pendente: "Pendente",
  verificado: "Verificado",
};

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-black/5 p-5 shadow-sm hover:-translate-y-0.5 transition-all duration-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">{label}</p>
          <p className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState({ total: 0, signed: 0, pending: 0, today_signed: 0 });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [statsRes, docsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/documents`),
      ]);
      setStats(statsRes.data);
      setDocuments(docsRes.data);
    } catch (e) {
      console.error("Erro ao carregar dados:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Remover documento?")) return;
    try {
      await axios.delete(`${API}/documents/${id}`);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDownload = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API}/documents/${id}/download/signed`);
      const { filename, content_base64 } = res.data;
      const bytes = atob(content_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const formatSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Dashboard
          </h1>
          <p className="text-gray-500 text-sm mt-1">Gerencie e acompanhe suas assinaturas digitais</p>
        </div>
        <button
          data-testid="btn-new-signature"
          onClick={() => navigate("/sign")}
          className="flex items-center gap-2 bg-[#163A24] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#205234] hover:-translate-y-0.5 transition-all duration-200 shadow-sm"
        >
          <FilePen size={15} />
          Nova Assinatura
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
        <StatCard icon={FileText} label="Total de Documentos" value={stats.total} color="bg-blue-50 text-blue-600" testId="stat-total" />
        <StatCard icon={ShieldCheck} label="Documentos Assinados" value={stats.signed} color="bg-green-50 text-green-600" testId="stat-signed" />
        <StatCard icon={TrendingUp} label="Assinados Hoje" value={stats.today_signed} color="bg-[#163A24]/10 text-[#163A24]" testId="stat-today" />
        <StatCard icon={Clock} label="Aguardando Assinatura" value={stats.pending} color="bg-yellow-50 text-yellow-600" testId="stat-pending" />
      </div>

      {/* Documents table */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm overflow-hidden" data-testid="documents-table">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
          <h2 className="font-semibold text-[#1A1A1A] text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
            Documentos Recentes
          </h2>
          <span className="text-xs text-gray-400">{documents.length} documento{documents.length !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <div className="w-6 h-6 border-2 border-[#163A24] border-t-transparent rounded-full animate-spin mr-3" />
            Carregando...
          </div>
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400" data-testid="empty-state">
            <FileText size={40} className="mb-3 opacity-30" />
            <p className="font-medium">Nenhum documento ainda</p>
            <p className="text-sm mt-1">Clique em "Nova Assinatura" para começar</p>
            <button
              onClick={() => navigate("/sign")}
              className="mt-4 flex items-center gap-2 text-[#163A24] text-sm font-semibold hover:underline"
            >
              Assinar primeiro documento <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-xs text-gray-400 font-semibold uppercase tracking-wider">Arquivo</th>
                  <th className="text-left px-6 py-3 text-xs text-gray-400 font-semibold uppercase tracking-wider">Tipo</th>
                  <th className="text-left px-6 py-3 text-xs text-gray-400 font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs text-gray-400 font-semibold uppercase tracking-wider">Assinante</th>
                  <th className="text-left px-6 py-3 text-xs text-gray-400 font-semibold uppercase tracking-wider">Data</th>
                  <th className="text-right px-6 py-3 text-xs text-gray-400 font-semibold uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    data-testid={`doc-row-${doc.id}`}
                    onClick={() => doc.status === "pendente" && navigate(`/sign/${doc.id}`)}
                    className={`border-b border-gray-50 last:border-0 transition-colors duration-150 ${doc.status === "pendente" ? "cursor-pointer hover:bg-[#FAF9F6]" : "hover:bg-gray-50/50"}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-800 truncate max-w-[160px]">{doc.filename}</span>
                        {doc.file_size && <span className="text-xs text-gray-400">({formatSize(doc.file_size)})</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-2 py-1 rounded border ${SIGN_TYPE_COLORS[doc.sign_type] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                        {doc.sign_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-2 py-1 rounded border ${STATUS_COLORS[doc.status] || "bg-gray-50 text-gray-500 border-gray-200"}`}>
                        {STATUS_LABELS[doc.status] || doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-xs">
                      {doc.signer_cn ? (
                        <span className="font-medium text-gray-700">{doc.signer_cn.split(":")[0]}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-xs">
                      {formatDate(doc.signed_at || doc.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 justify-end">
                        {doc.status === "assinado" && (
                          <button
                            data-testid={`btn-download-${doc.id}`}
                            onClick={(e) => handleDownload(doc.id, e)}
                            className="p-1.5 text-gray-400 hover:text-[#163A24] hover:bg-green-50 rounded transition-colors"
                            title="Baixar assinado"
                          >
                            <Download size={14} />
                          </button>
                        )}
                        {doc.status === "pendente" && (
                          <button
                            data-testid={`btn-sign-${doc.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/sign/${doc.id}`); }}
                            className="flex items-center gap-1 px-2.5 py-1 bg-[#163A24] text-white text-xs rounded hover:bg-[#205234] transition-colors"
                          >
                            <FilePen size={11} /> Assinar
                          </button>
                        )}
                        <button
                          data-testid={`btn-delete-${doc.id}`}
                          onClick={(e) => handleDelete(doc.id, e)}
                          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Remover"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
